'use client';
import Link from 'next/link';

import { HitType } from '@/modules/common/lib/abtastyStub';
import { ArrowLeft } from 'lucide-react';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import Image from '@/modules/common/components/Image';
import MarkdownRenderer from '@/modules/common/components/MarkdownRenderer';
import { Heart } from '@/modules/common/components/icons';
import useFeatureFlag from '@/modules/common/hooks/useFeatureFlag';
import { useQueryParams } from '@/modules/common/hooks/useQueryParams';
import { useAppSelector } from '@/modules/common/hooks/useStore';
import { ContentfulCollection } from '@/modules/product/models/collection.interface';
import { AddToShoppingListButton } from '@/modules/shopping-list/components/AddToShoppingListButton';
import { ShoppingListSaveType } from '@/modules/shopping-list/hooks/useShoppingListActions';
import { SaveType } from '@/modules/shopping-list/models/shoppingListTextItem.interface';
import { selectShoppingListProducts } from '@/modules/shopping-list/state/shoppingListSlice';

import {
  DataLayerFeature,
  addToWishlistDataLayerEvent,
} from '../../services/data-layer-events/add-to-wishlist';
import { CollectionDataLayer } from '../../services/data-layer-events/utils';
import { CollectionRoomExactMatchListCard } from './CollectionRoomExactMatchListCard';

export const CollectionRoomHeader = ({ collection }: { collection: ContentfulCollection }) => {
  const shoppingListProducts = useAppSelector(selectShoppingListProducts);
  const { sendFeatureFlagTracking } = useFeatureFlag();

  const [callBackUrl] = useQueryParams('callBackUrl', '');

  const { slug, collectionName: title, newBaseImage } = collection;

  const shoppingListData = {
    name: title ?? '',
    key: slug ?? '',
    saveType: SaveType.Collection,
  };

  const wishlistTrackingCallback = () => {
    addToWishlistDataLayerEvent({
      feature: DataLayerFeature.Collections,
      method: 'Single',
      type: ShoppingListSaveType.Collection,
      product: {
        slug: slug ?? '',
        title: title ?? '',
      } satisfies CollectionDataLayer,
      where: DataLayerWhere.Collections,
      shoppingListProducts,
      item_list_type: 'Room collection',
      item_list_name: title,
      item_list_id: slug,
    });

    sendFeatureFlagTracking({
      type: HitType.EVENT,
      category: 'Action Tracking',
      action: 'Collection Saved',
      label: collection.roomType,
    });
  };

  const CollectionImage = ({ className }: { className?: string }) => {
    return (
      <div className={`h-full w-full overflow-hidden rounded-xxl lg:max-w-[800px] ${className}`}>
        {collection?.newBaseImage?.url && (
          <Image
            width={collection.newBaseImage?.width ?? 800}
            height={collection.newBaseImage?.height ?? 400}
            src={collection.newBaseImage?.url}
            alt="Collection Banner"
            className="block h-full w-full object-contain"
          />
        )}
      </div>
    );
  };

  return (
    <div className="mx-auto w-full px-4 md:gap-8 md:px-8 lg:flex lg:justify-center">
      <div className="flex flex-col justify-between lg:max-w-[600px]">
        {callBackUrl && (
          <Link className="pointer group mb-4 flex items-center gap-1" href={callBackUrl}>
            <ArrowLeft className="h-4 w-4" />
            <span className="group-hover:underline">Back</span>
          </Link>
        )}
        <header className="mb-2 flex justify-between xl:mb-3">
          <h1
            className="text-[24px] font-bold xs:text-[28px] md:text-4xl"
            data-testid="collection-name"
          >
            {collection?.collectionName || ''}
          </h1>
          <div className="">
            <AddToShoppingListButton
              saveType={ShoppingListSaveType.Collection}
              textLineItem={shoppingListData}
              image={newBaseImage?.url ?? ''}
              className="static border border-gray-75 bg-white"
              variant="secondary"
              sku={undefined}
              callbacks={{ afterAdd: wishlistTrackingCallback }}
              data-testid="add-to-shopping-list-button"
            >
              <Heart className="p-1" />
            </AddToShoppingListButton>
          </div>
        </header>
        <MarkdownRenderer
          className="mx-auto text-left md:mb-3 lg:max-w-[912px] lg:text-lg xl:mb-9 xl:text-2xl"
          data-testid="collection-description"
        >
          {collection?.description}
        </MarkdownRenderer>

        <CollectionImage className="my-4 block rounded-sm lg:hidden" />

        <CollectionRoomExactMatchListCard />
      </div>

      <CollectionImage className="hidden h-full lg:block" />
    </div>
  );
};
